import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  FaAmbulance,
  FaBaby,
  FaCertificate,
  FaCheckCircle,
  FaExclamationTriangle,
  FaHeartbeat,
  FaHospital,
  FaInfinity,
  FaShieldAlt,
  FaTimesCircle,
  FaUserNurse,
} from "react-icons/fa";

import { API_BASE_URL } from "../../config/apiConfig";

import PageLoader from "../../components/common/PageLoader";


import "../../styles/pages/professional/ProfessionalCertifications.css";


const CERTIFICATIONS_URL =
  `${API_BASE_URL}/professionals/certifications`;

const initialForm = {
  certificationId: "",
  certificateNumber: "",
  issuingOrganization: "",
  issueDate: "",
  expiryDate: "",
  neverExpires: false,
};

const statusFilters = [
  "All",
  "Current",
  "Expiring Soon",
  "Expired",
  "No Expiry",
];

const ProfessionalCertifications = () => {
  const [
    certificationOptions,
    setCertificationOptions,
  ] = useState([]);

  const [
    certifications,
    setCertifications,
  ] = useState([]);

  const [form, setForm] =
    useState(initialForm);

  const [
    editingCertification,
    setEditingCertification,
  ] = useState(null);

  const [showForm, setShowForm] =
    useState(false);

  const [loading, setLoading] =
    useState(true);

  const [saving, setSaving] =
    useState(false);

  const [deletingId, setDeletingId] =
    useState(null);

  const [error, setError] =
    useState("");

  const [
    successMessage,
    setSuccessMessage,
  ] = useState("");

  const [
    showLoadingIndicator,
    setShowLoadingIndicator,
  ] = useState(false);

  const [searchTerm, setSearchTerm] =
    useState("");

  const [statusFilter, setStatusFilter] =
    useState("All");

  const [sortBy, setSortBy] =
    useState("expiry");

  const getToken = () => {
    const token =
      localStorage.getItem(
        "zethAuthToken"
      ) ||
      sessionStorage.getItem(
        "zethAuthToken"
      );

    if (!token) {
      throw new Error(
        "Authentication required. Please sign in again."
      );
    }

    return token;
  };

  const parseResponse = async (
    response
  ) => {
    const contentType =
      response.headers.get(
        "content-type"
      );

    if (
      !contentType?.includes(
        "application/json"
      )
    ) {
      throw new Error(
        "The server returned an invalid response."
      );
    }

    return response.json();
  };

  const loadCertifications =
    useCallback(async () => {
      try {
        setLoading(true);
        setError("");

        const token = getToken();

        const [
          optionsResponse,
          certificationsResponse,
        ] = await Promise.all([
          fetch(
            `${CERTIFICATIONS_URL}/options`,
            {
              headers: {
                Authorization:
                  `Bearer ${token}`,
              },
            }
          ),

          fetch(
            CERTIFICATIONS_URL,
            {
              headers: {
                Authorization:
                  `Bearer ${token}`,
              },
            }
          ),
        ]);

        const [
          optionsData,
          certificationsData,
        ] = await Promise.all([
          parseResponse(
            optionsResponse
          ),
          parseResponse(
            certificationsResponse
          ),
        ]);

        if (
          !optionsResponse.ok ||
          !optionsData.success
        ) {
          throw new Error(
            optionsData.message ||
              "Unable to load certification options."
          );
        }

        if (
          !certificationsResponse.ok ||
          !certificationsData.success
        ) {
          throw new Error(
            certificationsData.message ||
              "Unable to load your certifications."
          );
        }

        setCertificationOptions(
          Array.isArray(
            optionsData.certifications
          )
            ? optionsData.certifications
            : []
        );

        setCertifications(
          Array.isArray(
            certificationsData.certifications
          )
            ? certificationsData.certifications
            : []
        );
      } catch (loadError) {
        console.error(
          "Load certifications error:",
          loadError
        );

        setError(
          loadError.message ||
            "Unable to load certifications."
        );
      } finally {
        setLoading(false);
      }
    }, []);

  useEffect(() => {
    loadCertifications();
  }, [loadCertifications]);

  useEffect(() => {
    if (!loading) {
      setShowLoadingIndicator(false);
      return undefined;
    }

    const timer =
      window.setTimeout(() => {
        setShowLoadingIndicator(true);
      }, 800);

    return () => {
      window.clearTimeout(timer);
    };
  }, [loading]);

  const clearMessages = () => {
    setError("");
    setSuccessMessage("");
  };

  const handleInputChange = (
    event
  ) => {
    const {
      name,
      value,
      type,
      checked,
    } = event.target;

    setForm((currentForm) => {
      if (name === "neverExpires") {
        return {
          ...currentForm,
          neverExpires: checked,
          expiryDate: checked
            ? ""
            : currentForm.expiryDate,
        };
      }

      return {
        ...currentForm,
        [name]:
          type === "checkbox"
            ? checked
            : value,
      };
    });

    clearMessages();
  };

  const handleOpenAddForm = () => {
    setEditingCertification(null);
    setForm({
      ...initialForm,
    });
    clearMessages();
    setShowForm(true);
  };

  const handleEdit = (
    certification
  ) => {
    setEditingCertification(
      certification
    );

    setForm({
      certificationId:
        certification.certification_id
          ? String(
              certification.certification_id
            )
          : "",

      certificateNumber:
        certification.certificate_number ||
        "",

      issuingOrganization:
        certification.issuing_organization ||
        "",

      issueDate:
        certification.issue_date
          ? String(
              certification.issue_date
            ).slice(0, 10)
          : "",

      expiryDate:
        certification.expiry_date
          ? String(
              certification.expiry_date
            ).slice(0, 10)
          : "",

      neverExpires:
        !certification.expiry_date,
    });

    clearMessages();
    setShowForm(true);
  };

  const handleCloseForm = () => {
    if (saving) {
      return;
    }

    setShowForm(false);
    setEditingCertification(null);
    setForm({
      ...initialForm,
    });
    setError("");
  };

  const validateForm = () => {
    if (!form.certificationId) {
      throw new Error(
        "Please select a certification."
      );
    }

    if (
      !form.issuingOrganization.trim()
    ) {
      throw new Error(
        "Please enter the issuing organization."
      );
    }

    if (!form.issueDate) {
      throw new Error(
        "Please enter the issue date."
      );
    }

    if (
      !form.neverExpires &&
      !form.expiryDate
    ) {
      throw new Error(
        "Please enter the expiry date or select that the certification does not expire."
      );
    }

    if (
      !form.neverExpires &&
      form.expiryDate <
        form.issueDate
    ) {
      throw new Error(
        "The expiry date cannot be earlier than the issue date."
      );
    }
  };

  const handleSubmit = async (
    event
  ) => {
    event.preventDefault();

    try {
      setSaving(true);
      clearMessages();

      validateForm();

      const token = getToken();

      const isEditing = Boolean(
        editingCertification?.id
      );

      const requestUrl = isEditing
        ? `${CERTIFICATIONS_URL}/${editingCertification.id}`
        : CERTIFICATIONS_URL;

      const response = await fetch(
        requestUrl,
        {
          method: isEditing
            ? "PUT"
            : "POST",

          headers: {
            "Content-Type":
              "application/json",

            Authorization:
              `Bearer ${token}`,
          },

          body: JSON.stringify({
            certificationId: Number(
              form.certificationId
            ),

            certificateNumber:
              form.certificateNumber.trim() ||
              null,

            issuingOrganization:
              form.issuingOrganization.trim(),

            issueDate:
              form.issueDate,

            expiryDate:
              form.neverExpires
                ? null
                : form.expiryDate,

            neverExpires:
              form.neverExpires,
          }),
        }
      );

      const data =
        await parseResponse(response);

      if (
        !response.ok ||
        !data.success
      ) {
        throw new Error(
          data.message ||
            "Unable to save certification."
        );
      }

      setSuccessMessage(
        data.message ||
          `Certification ${
            isEditing
              ? "updated"
              : "added"
          } successfully.`
      );

      setShowForm(false);
      setEditingCertification(null);
      setForm({
        ...initialForm,
      });

      await loadCertifications();
    } catch (saveError) {
      console.error(
        "Save certification error:",
        saveError
      );

      setError(
        saveError.message ||
          "Unable to save certification."
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (
    certification
  ) => {
    const confirmed =
      window.confirm(
        `Remove ${
          certification.code ||
          certification.name ||
          "this certification"
        }?`
      );

    if (!confirmed) {
      return;
    }

    try {
      setDeletingId(
        certification.id
      );

      clearMessages();

      const token = getToken();

      const response = await fetch(
        `${CERTIFICATIONS_URL}/${certification.id}`,
        {
          method: "DELETE",

          headers: {
            Authorization:
              `Bearer ${token}`,
          },
        }
      );

      const data =
        await parseResponse(response);

      if (
        !response.ok ||
        !data.success
      ) {
        throw new Error(
          data.message ||
            "Unable to remove certification."
        );
      }

      setCertifications(
        (currentCertifications) =>
          currentCertifications.filter(
            (item) =>
              item.id !==
              certification.id
          )
      );

      setSuccessMessage(
        data.message ||
          "Certification removed successfully."
      );
    } catch (deleteError) {
      console.error(
        "Delete certification error:",
        deleteError
      );

      setError(
        deleteError.message ||
          "Unable to remove certification."
      );
    } finally {
      setDeletingId(null);
    }
  };

  const formatDate = (
    dateValue
  ) => {
    if (!dateValue) {
      return "Not provided";
    }

    const date = new Date(
      dateValue
    );

    if (
      Number.isNaN(
        date.getTime()
      )
    ) {
      return "Not provided";
    }

    return new Intl.DateTimeFormat(
      "en-CA",
      {
        year: "numeric",
        month: "short",
        day: "numeric",
      }
    ).format(date);
  };

  const getCertificationStatus = (
    certification
  ) => {
    if (
      !certification.expiry_date
    ) {
      return "No Expiry";
    }

    const today = new Date();
    today.setHours(
      0,
      0,
      0,
      0
    );

    const expiryDate = new Date(
      certification.expiry_date
    );

    expiryDate.setHours(
      0,
      0,
      0,
      0
    );

    const millisecondsPerDay =
      1000 * 60 * 60 * 24;

    const daysUntilExpiry =
      Math.ceil(
        (expiryDate - today) /
          millisecondsPerDay
      );

    if (daysUntilExpiry < 0) {
      return "Expired";
    }

    if (daysUntilExpiry <= 60) {
      return "Expiring Soon";
    }

    return "Current";
  };

  const getCertificationStatusIcon = (
    status
  ) => {
    switch (status) {
      case "Current":
        return <FaCheckCircle />;

      case "Expiring Soon":
        return (
          <FaExclamationTriangle />
        );

      case "Expired":
        return <FaTimesCircle />;

      case "No Expiry":
        return <FaInfinity />;

      default:
        return null;
    }
  };

  const getCertificationIcon = (
    code
  ) => {
    switch (
      String(
        code || ""
      ).toUpperCase()
    ) {
      case "BLS":
      case "ACLS":
        return <FaHeartbeat />;

      case "PALS":
      case "NRP":
        return <FaBaby />;

      case "CTAS":
        return <FaAmbulance />;

      case "TNCC":
        return <FaHospital />;

      case "CPI":
        return <FaShieldAlt />;

      case "RN":
        return <FaUserNurse />;

      default:
        return <FaCertificate />;
    }
  };

  const certificationSummary =
    useMemo(() => {
      return certifications.reduce(
        (summary, certification) => {
          const status =
            getCertificationStatus(
              certification
            );

          summary.total += 1;

          if (status === "Current") {
            summary.current += 1;
          }

          if (
            status ===
            "Expiring Soon"
          ) {
            summary.expiringSoon += 1;
          }

          if (status === "Expired") {
            summary.expired += 1;
          }

          if (
            status === "No Expiry"
          ) {
            summary.noExpiry += 1;
          }

          return summary;
        },
        {
          total: 0,
          current: 0,
          expiringSoon: 0,
          expired: 0,
          noExpiry: 0,
        }
      );
    }, [certifications]);

  const filteredCertifications =
    useMemo(() => {
      const normalizedSearch =
        searchTerm
          .trim()
          .toLowerCase();

      const filtered =
        certifications.filter(
          (certification) => {
            const searchableValues = [
              certification.code,
              certification.name,
              certification.certification_name,
              certification.issuing_organization,
              certification.certificate_number,
            ];

            const matchesSearch =
              !normalizedSearch ||
              searchableValues.some(
                (value) =>
                  String(
                    value || ""
                  )
                    .toLowerCase()
                    .includes(
                      normalizedSearch
                    )
              );

            if (!matchesSearch) {
              return false;
            }

            if (
              statusFilter === "All"
            ) {
              return true;
            }

            return (
              getCertificationStatus(
                certification
              ) === statusFilter
            );
          }
        );

      return [...filtered].sort(
        (first, second) => {
          if (sortBy === "name") {
            const firstName =
              first.name ||
              first.certification_name ||
              first.code ||
              "";

            const secondName =
              second.name ||
              second.certification_name ||
              second.code ||
              "";

            return firstName.localeCompare(
              secondName,
              "en-CA",
              {
                sensitivity: "base",
              }
            );
          }

          if (sortBy === "expiry") {
            const firstExpiry =
              first.expiry_date
                ? new Date(
                    first.expiry_date
                  ).getTime()
                : Number.MAX_SAFE_INTEGER;

            const secondExpiry =
              second.expiry_date
                ? new Date(
                    second.expiry_date
                  ).getTime()
                : Number.MAX_SAFE_INTEGER;

            return (
              firstExpiry -
              secondExpiry
            );
          }

          if (sortBy === "issueDate") {
            const firstIssueDate =
              first.issue_date
                ? new Date(
                    first.issue_date
                  ).getTime()
                : 0;

            const secondIssueDate =
              second.issue_date
                ? new Date(
                    second.issue_date
                  ).getTime()
                : 0;

            return (
              secondIssueDate -
              firstIssueDate
            );
          }

          return 0;
        }
      );
    }, [
      certifications,
      searchTerm,
      statusFilter,
      sortBy,
    ]);

  const clearCertificationFilters =
    () => {
      setSearchTerm("");
      setStatusFilter("All");
      setSortBy("expiry");
    };

  if (
    loading &&
    showLoadingIndicator
  ) {
    return (
      <PageLoader
        title="Professional Certifications"
        message="Loading your certifications..."
      />
    );
  }

  if (loading) {
    return null;
  }

  return (
    <section className="credential-child-page professional-certifications-page">
     

      <header className="credential-child-header">
  <div>
    <p>
      Manage your certifications and keep expiry dates current to remain assignment-ready.
    </p>
  </div>

  <button
    type="button"
    className="credential-child-action-button"
    onClick={handleOpenAddForm}
  >
    + Add Certification
  </button>
</header>

      <section
        className="certification-summary-grid"
        aria-label="Certification summary"
      >
        <article className="summary-card">
          <span className="summary-label">
            Total Certifications
          </span>

          <h2>
            {certificationSummary.total}
          </h2>

          <small>
            Certifications added
          </small>
        </article>

        <article className="summary-card">
          <span className="summary-label">
            Current
          </span>

          <h2>
            {certificationSummary.current +
              certificationSummary.noExpiry}
          </h2>

          <small>
            Current or no expiry
          </small>
        </article>

        <article className="summary-card">
          <span className="summary-label">
            Expiring Soon
          </span>

          <h2>
            {
              certificationSummary.expiringSoon
            }
          </h2>

          <small>
            Expiring within 60 days
          </small>
        </article>

        <article className="summary-card">
          <span className="summary-label">
            Expired
          </span>

          <h2>
            {certificationSummary.expired}
          </h2>

          <small>
            Renewal required
          </small>
        </article>
      </section>

      {certifications.length > 0 && (
        <section
          className="certification-toolbar"
          aria-label="Search and filter certifications"
        >
          <div className="certification-search-field">
            <label
              htmlFor="certification-search"
              className="sr-only"
            >
              Search certifications
            </label>

            <input
              id="certification-search"
              type="search"
              placeholder="Search by certification, organization or certificate number..."
              value={searchTerm}
              onChange={(event) =>
                setSearchTerm(
                  event.target.value
                )
              }
            />
          </div>

          <div
            className="certification-filter-group"
            aria-label="Certification status filters"
          >
            {statusFilters.map(
              (status) => (
                <button
                  key={status}
                  type="button"
                  className={
                    statusFilter ===
                    status
                      ? "filter-chip active"
                      : "filter-chip"
                  }
                  onClick={() =>
                    setStatusFilter(
                      status
                    )
                  }
                >
                  {status}
                </button>
              )
            )}
          </div>

          <div className="certification-sort-field">
            <label htmlFor="certification-sort">
              Sort by
            </label>

            <select
              id="certification-sort"
              value={sortBy}
              onChange={(event) =>
                setSortBy(
                  event.target.value
                )
              }
            >
              <option value="expiry">
                Expiry date
              </option>

              <option value="name">
                Name (A–Z)
              </option>

              <option value="issueDate">
                Newest issue date
              </option>
            </select>
          </div>
        </section>
      )}

      {error && (
        <div
          className="certification-message certification-message-error"
          role="alert"
        >
          {error}
        </div>
      )}

      {successMessage && (
        <div
          className="certification-message certification-message-success"
          role="status"
        >
          {successMessage}
        </div>
      )}

      {certifications.length === 0 ? (
        <section className="certifications-empty-state">
          <div
            className="certifications-empty-icon"
            aria-hidden="true"
          >
            ✓
          </div>

          <h3>
            No certifications added
          </h3>

          <p>
            Add certifications such as BLS, ACLS,
            PALS, NRP, CTAS, TNCC or other relevant
            professional credentials.
          </p>

          <button
            type="button"
            className="add-certification-button"
            onClick={handleOpenAddForm}
          >
            Add Your First Certification
          </button>
        </section>
      ) : filteredCertifications.length ===
        0 ? (
        <section className="certifications-empty-state">
          <div
            className="certifications-empty-icon"
            aria-hidden="true"
          >
            🔍
          </div>

          <h3>
            No matching certifications
          </h3>

          <p>
            No certifications match your current
            search or status filter.
          </p>

          <button
            type="button"
            className="certification-edit-button"
            onClick={
              clearCertificationFilters
            }
          >
            Clear Search and Filters
          </button>
        </section>
      ) : (
        <section className="certifications-grid">
          {filteredCertifications.map(
            (certification) => {
              const status =
                getCertificationStatus(
                  certification
                );

              return (
                <article
                  className="certification-card"
                  key={certification.id}
                >
                  <div className="certification-card-top">
                    <div className="certification-title-group">
                      <div className="certification-icon">
                        {getCertificationIcon(
                          certification.code
                        )}
                      </div>

                      <div>
                        <span className="certification-code">
                          {certification.code ||
                            "Certification"}
                        </span>

                        <h3>
                          {certification.name ||
                            certification.certification_name ||
                            "Professional Certification"}
                        </h3>
                      </div>
                    </div>

                    <span
                      className={`certification-status certification-status-${status
                        .toLowerCase()
                        .replace(
                          /\s+/g,
                          "-"
                        )}`}
                    >
                      {getCertificationStatusIcon(
                        status
                      )}

                      <span>
                        {status}
                      </span>
                    </span>
                  </div>

                  <dl className="certification-details">
                    <div>
                      <dt>
                        Certificate Number
                      </dt>

                      <dd>
                        {certification.certificate_number ||
                          "Not provided"}
                      </dd>
                    </div>

                    <div>
                      <dt>
                        Issuing Organization
                      </dt>

                      <dd>
                        {certification.issuing_organization ||
                          "Not provided"}
                      </dd>
                    </div>

                    <div>
                      <dt>
                        Issue Date
                      </dt>

                      <dd>
                        {formatDate(
                          certification.issue_date
                        )}
                      </dd>
                    </div>

                    <div>
                      <dt>
                        Expiry Date
                      </dt>

                      <dd>
                        {certification.expiry_date
                          ? formatDate(
                              certification.expiry_date
                            )
                          : "Does not expire"}
                      </dd>
                    </div>

                    <div>
                      <dt>
                        Verification
                      </dt>

                      <dd>
                        <span className="verification-status">
                          {certification.verification_status ||
                            "Pending"}
                        </span>
                      </dd>
                    </div>
                  </dl>

                  <div className="certification-card-actions">
                    <button
                      type="button"
                      className="certification-edit-button"
                      onClick={() =>
                        handleEdit(
                          certification
                        )
                      }
                    >
                      Edit
                    </button>

                    <button
                      type="button"
                      className="certification-delete-button"
                      onClick={() =>
                        handleDelete(
                          certification
                        )
                      }
                      disabled={
                        deletingId ===
                        certification.id
                      }
                    >
                      {deletingId ===
                      certification.id
                        ? "Removing..."
                        : "Remove"}
                    </button>
                  </div>
                </article>
              );
            }
          )}
        </section>
      )}

      {showForm && (
        <div
          className="certification-modal-overlay"
          role="presentation"
          onMouseDown={(event) => {
            if (
              event.target ===
              event.currentTarget
            ) {
              handleCloseForm();
            }
          }}
        >
          <div
            className="certification-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="certification-modal-title"
          >
            <div className="certification-modal-header">
              <div>
                <p className="account-label">
                  Professional Credentials
                </p>

                <h2 id="certification-modal-title">
                  {editingCertification
                    ? "Edit Certification"
                    : "Add Certification"}
                </h2>
              </div>

              <button
                type="button"
                className="certification-modal-close"
                onClick={handleCloseForm}
                disabled={saving}
                aria-label="Close certification form"
              >
                ×
              </button>
            </div>

            <form
              onSubmit={handleSubmit}
              className="certification-form"
            >
              <div className="certification-form-grid">
                <div className="certification-form-group certification-form-full">
                  <label htmlFor="certificationId">
                    Certification *
                  </label>

                  <select
                    id="certificationId"
                    name="certificationId"
                    value={
                      form.certificationId
                    }
                    onChange={
                      handleInputChange
                    }
                    disabled={
                      saving ||
                      Boolean(
                        editingCertification
                      )
                    }
                    required
                  >
                    <option value="">
                      Select certification
                    </option>

                    {certificationOptions.map(
                      (option) => (
                        <option
                          key={option.id}
                          value={option.id}
                        >
                          {option.code} —{" "}
                          {option.name}
                        </option>
                      )
                    )}
                  </select>
                </div>

                <div className="certification-form-group">
                  <label htmlFor="certificateNumber">
                    Certificate Number
                  </label>

                  <input
                    id="certificateNumber"
                    name="certificateNumber"
                    type="text"
                    value={
                      form.certificateNumber
                    }
                    onChange={
                      handleInputChange
                    }
                    placeholder="Optional"
                  />
                </div>

                <div className="certification-form-group">
                  <label htmlFor="issuingOrganization">
                    Issuing Organization *
                  </label>

                  <input
                    id="issuingOrganization"
                    name="issuingOrganization"
                    type="text"
                    value={
                      form.issuingOrganization
                    }
                    onChange={
                      handleInputChange
                    }
                    placeholder="e.g. Heart and Stroke Foundation"
                    required
                  />
                </div>

                <div className="certification-form-group">
                  <label htmlFor="issueDate">
                    Issue Date *
                  </label>

                  <input
                    id="issueDate"
                    name="issueDate"
                    type="date"
                    value={form.issueDate}
                    onChange={
                      handleInputChange
                    }
                    required
                  />
                </div>

                <div className="certification-expiry-option">
                  <input
                    id="neverExpires"
                    name="neverExpires"
                    type="checkbox"
                    checked={
                      form.neverExpires
                    }
                    onChange={
                      handleInputChange
                    }
                    disabled={saving}
                  />

                  <label htmlFor="neverExpires">
                    This certification does not expire
                  </label>
                </div>

                <div className="certification-form-group">
                  <label htmlFor="expiryDate">
                    Expiry Date
                    {!form.neverExpires &&
                      " *"}
                  </label>

                  <input
                    id="expiryDate"
                    name="expiryDate"
                    type="date"
                    value={
                      form.expiryDate
                    }
                    onChange={
                      handleInputChange
                    }
                    min={
                      form.issueDate ||
                      undefined
                    }
                    disabled={
                      saving ||
                      form.neverExpires
                    }
                    required={
                      !form.neverExpires
                    }
                  />
                </div>
              </div>

              <div className="certification-modal-actions">
                <button
                  type="button"
                  className="certification-cancel-button"
                  onClick={handleCloseForm}
                  disabled={saving}
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  className="certification-save-button"
                  disabled={saving}
                >
                  {saving
                    ? "Saving..."
                    : editingCertification
                      ? "Update Certification"
                      : "Save Certification"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </section>
  );
};

export default ProfessionalCertifications;
